/**
 * Auth Guard Component
 *
 * Protects routes by requiring OIDC authentication.
 * Automatically redirects to IDP login when not authenticated.
 *
 * Similar to AdminGuard but without admin-specific context.
 */

import { useEffect, useRef, useState } from "react";
import { Outlet } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Button,
} from "@mui/material";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import { useAuth } from "react-oidc-context";
import {
  authReturnPath,
  isPasswordChangeArrival,
  newPasswordChangeReauthToken,
  PASSWORD_CHANGE_REAUTH_KEY,
  passwordChangeReauthStep,
  passwordChangeSigninArgs,
} from "../auth/passwordChangeSignin";
import { AUTH_RETURN_TO_KEY } from "../auth/returnTo";
import { getConfigSync } from "../config";

/** Survive a remount of the page that started the redirect. A new document load resets both. */
let reauthStartedOnThisPage = false;
let reauthConsumed = false;

function rememberReturnPath() {
  sessionStorage.setItem(
    AUTH_RETURN_TO_KEY,
    authReturnPath(window.location.pathname, window.location.search)
  );
}

function signinFailureMessage(err: unknown): string {
  return err instanceof Error && err.message
    ? err.message
    : "Unable to start the Sovra sign-in. Please try again.";
}

function changePasswordSignin(search: string, reauthToken?: string) {
  return passwordChangeSigninArgs(
    search,
    reauthToken,
    getConfigSync().oidc.audience
  );
}

function startPasswordChangeSignin(
  auth: ReturnType<typeof useAuth>,
  onFailure: (message: string) => void
) {
  const token = newPasswordChangeReauthToken();
  reauthStartedOnThisPage = true;
  sessionStorage.setItem(PASSWORD_CHANGE_REAUTH_KEY, token);
  rememberReturnPath();
  auth
    .signinRedirect(changePasswordSignin(window.location.search, token))
    .catch((err: unknown) => {
      reauthStartedOnThisPage = false;
      sessionStorage.removeItem(PASSWORD_CHANGE_REAUTH_KEY);
      onFailure(signinFailureMessage(err));
    });
}

/**
 * Auth Guard - wraps routes requiring OIDC authentication.
 * A change-password arrival forces one fresh sign-in even when a session already exists.
 */
export function AuthGuard() {
  const auth = useAuth();
  const loginInitiated = useRef(false);
  const [reauthDone, setReauthDone] = useState(reauthConsumed);
  const [redirectError, setRedirectError] = useState<string | null>(null);

  const isLoading = auth.isLoading;
  const isAuthenticated = auth.isAuthenticated;
  const error = auth.error?.message ?? redirectError;
  const returnedState = auth.user?.state;

  // Automatically redirect to OIDC provider when not authenticated.
  // A change-password arrival also redirects when a session already exists, once.
  useEffect(() => {
    if (isLoading || error) {
      return;
    }
    const search = window.location.search;
    const step = passwordChangeReauthStep({
      search,
      isAuthenticated,
      expectedToken: sessionStorage.getItem(PASSWORD_CHANGE_REAUTH_KEY),
      returnedState,
      startedHere: reauthStartedOnThisPage,
      consumed: reauthConsumed,
    });
    if (step === "complete") {
      sessionStorage.removeItem(PASSWORD_CHANGE_REAUTH_KEY);
      reauthConsumed = true;
      setReauthDone(true);
      return;
    }
    if (step === "redirect") {
      startPasswordChangeSignin(auth, setRedirectError);
      return;
    }
    if (!isAuthenticated && !loginInitiated.current) {
      loginInitiated.current = true;
      rememberReturnPath();
      auth
        .signinRedirect(changePasswordSignin(search))
        .catch((err: unknown) => {
          loginInitiated.current = false;
          setRedirectError(signinFailureMessage(err));
        });
    }
  }, [isAuthenticated, isLoading, error, returnedState, auth]);

  // Error state - show this first as it's most important
  if (error) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            minHeight: "60vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
          }}
        >
          <VerifiedUserIcon sx={{ fontSize: 64, color: "error.main", mb: 2 }} />
          <Typography variant="h5" color="error" gutterBottom>
            Authentication Error
          </Typography>
          <Alert severity="error" sx={{ mb: 3, maxWidth: 400 }}>
            {error}
          </Alert>
          <Button
            variant="contained"
            onClick={() => {
              loginInitiated.current = false;
              setRedirectError(null);
              if (isPasswordChangeArrival(window.location.search)) {
                startPasswordChangeSignin(auth, setRedirectError);
                return;
              }
              rememberReturnPath();
              auth
                .signinRedirect(changePasswordSignin(window.location.search))
                .catch((err: unknown) => {
                  setRedirectError(signinFailureMessage(err));
                });
            }}
          >
            Try Again
          </Button>
        </Box>
      </Container>
    );
  }

  // Authenticated - render child routes, unless this arrival still owes a fresh sign-in.
  const holdForReauth =
    isPasswordChangeArrival(window.location.search) && !reauthDone;
  if (isAuthenticated && auth.user && !holdForReauth) {
    return <Outlet />;
  }

  // Loading or redirecting - show unified "Authenticating..." message
  // This combines the "Loading..." and "Redirecting to login..." states
  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: "60vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={48} />
        <Typography sx={{ mt: 2 }} color="text.secondary">
          Authenticating...
        </Typography>
      </Box>
    </Container>
  );
}

export default AuthGuard;
